import { useSortable } from "@dnd-kit/sortable"
import { SortableContext } from "@dnd-kit/sortable"
import type { Activities as Column } from "../../../entities/activities"
import { Inbox } from "lucide-react"
import ActivitiesWeeklyCardSortable from "./activities-weekly-card-sortable"
import { cn } from "@/lib/utils"
import { useWeeklyActivities } from "@/modules/study/stores/use-weekly-activities-store"

interface ActivitiesWeeklyColumnRescheduledProps {
  column: Column
  isActive: boolean
  isDragging: boolean
  isPending: boolean
  sourceColumn: Column | null
}

export default function ActivitiesWeeklyColumnRescheduled({
  column,
  isActive,
  isDragging,
  isPending,
  sourceColumn,
}: ActivitiesWeeklyColumnRescheduledProps) {
  const { isNextWeek } = useWeeklyActivities()

  const { setNodeRef } = useSortable({
    id: `column-${column.id}`,
    data: {
      type: "column",
      column,
    },
  })

  const EmptyState = () =>
    isNextWeek ? (
      <div className="relative top-[-15px] flex h-full flex-col items-center space-y-2 px-2 pt-[50%]">
        <Inbox size={42} />
        <span className="flex flex-col gap-2 text-center">
          <p className="font-bold">No hay actividades</p>
          <p className="text-sm font-normal text-neutral-500">
            Acá van a aparecer las actividades que pasaste de la semana anterior.
          </p>
        </span>
      </div>
    ) : (
      <div className="relative top-[-15px] flex h-full flex-col items-center space-y-2 px-2 pt-[50%]">
        <Inbox size={42} />
        <span className="flex flex-col gap-2 text-center">
          <p className="font-bold">No hay actividades</p>
          <p className="text-sm font-normal text-neutral-500">
            Arrastrá las actividades que te quedaron pendientes de esta semana y organizate para la
            próxima.
          </p>
        </span>
      </div>
    )

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[500px] w-full select-none rounded-[10px] p-3 md:w-[300px]",
        isActive
          ? "border-2 border-primary bg-primary-50"
          : isDragging
            ? "border-2 border-primary"
            : "border-2 border-transparent bg-neutral-100",
      )}
    >
      <div className="flex w-full justify-center px-1">
        <p
          className={cn(
            "text-md mb-10 flex items-center gap-2 font-normal capitalize",
            isDragging ? "text-primary" : "text-neutral-700",
          )}
        >
          {isNextWeek ? "Actividades postergadas" : "Próxima semana"}
        </p>
      </div>
      <div className="relative top-[-15px] flex flex-col space-y-2">
        <SortableContext items={column.activities.map((act) => act.id)} strategy={() => null}>
          {column.activities.length
            ? column.activities.map((activity) => (
                <ActivitiesWeeklyCardSortable
                  key={activity.id}
                  activity={activity}
                  areDragging={isDragging}
                  isPending={isPending}
                  columnDate={column.date}
                  sourceColumn={sourceColumn}
                />
              ))
            : null}
        </SortableContext>
        {column.activities.length ? null : <EmptyState />}
      </div>
    </div>
  )
}
